import {
  put, call, all, race, take, takeLatest, delay, select
} from 'redux-saga/effects';
import { NETWORK_ERROR, UNKNOWN_ERROR } from 'constants/errorCodes';
import env from 'config/environment';
import { SagaIterator } from 'redux-saga';
import { api } from 'services/api';
import { RootActionTypes } from '@redux/modules/actionTypes';
import { FetchStatusSuccessAction } from '../api/actions';
import {
  showSessionWarning,
  renewSessionSuccess,
  logoutRequest,
  loginSuccess,
  loginFailure,
  renewSessionRequest,
  LogoutRequestAction,
  LoginFailureResponse,
  logoutSuccess,
  logoutFailure,
  LoginRequestAction,
  loggedIn,
  loggedOff,
  manageSessionTimeout,
} from './actions';
import * as ActionTypes from './actionTypes';
import { rootSelectors } from '../selectors';

/**
 * This is exported and stored in an object for testing.
 */
const constants = {
  timeoutLength: env.SessionTimeout(),
  oneMinute: 60000,
};

export function* onLogout(timedOut: boolean): SagaIterator {
  const defaultLogout = (env.SamlLogoutEnabled() && !(env.SamlEnabled() && env.Saml2Enabled()) ? env.SamlLogoutURL() : '/login?logout=true');
  const redirectPath = timedOut ? '/token' : defaultLogout;

  if (env.SamlLogoutEnabled()) {
    yield call((path) => { window.location.href = path; }, redirectPath);
  } else {
    // @ts-ignore - env.History's types need to be updated
    yield call(env.History().push, redirectPath); // eslint-disable-line @typescript-eslint/unbound-method
  }
}

export function* onLogoutRequest(action: LogoutRequestAction): SagaIterator {
  // Regardless if it fails or succeeds, the application will always log you off
  // locally.
  try {
    if (env.SamlLogoutEnabled()) { yield call(api.samlLogout); } else { yield call(api.logout); }

    yield put(logoutSuccess());
    yield put(loggedOff());
    yield call(onLogout, action.payload.timedOut);
  } catch (err) {
    yield put(logoutFailure());
    yield put(loggedOff());
    yield call(onLogout, action.payload.timedOut);

    if (action.payload.timedOut === true) {
      // do nothing
    } else {
      console.warn('onLogoutRequest failed:', err.msg);
    }
  }
}


export function* sessionTimeout(): SagaIterator {
  yield delay((constants.timeoutLength - 1) * constants.oneMinute);

  yield put(showSessionWarning());
  yield delay(constants.oneMinute);

  yield put(logoutRequest(true));
}

export function* onLoginRequest(action: LoginRequestAction): SagaIterator {
  const { username, password } = action.payload;

  try {
    const response = yield call(api.login, username, password);

    yield put(loginSuccess(response, new Date()));
    yield put(loggedIn());
  } catch (error) {
    const response = error.response as LoginFailureResponse;

    if (response) {
      const { data, status } = response;

      if (data && data.errors) {
        yield put(loginFailure(data.errors));
      } else {
        yield put(
          loginFailure([
            {
              message: UNKNOWN_ERROR,
              code: UNKNOWN_ERROR,
              status,
            },
          ])
        );
      }
    } else {
      // No response - API unreachable
      yield put(loginFailure([{ message: NETWORK_ERROR, code: NETWORK_ERROR }], error));
    }
  }
}

export function* onRenewSessionRequest(): SagaIterator {
  try {
    const token = yield select(rootSelectors.authentication.getToken);
    yield call(api.refresh, token);
    yield put(renewSessionSuccess(new Date()));
  } catch (error) {
    yield put(logoutRequest(true));
  }
}

export function* watcherWhenLoggedIn(): SagaIterator {
  yield takeLatest(
    [ActionTypes.FETCH_RENEW_SESSION_SUCCESS, ActionTypes.MANAGE_SESSION_TIMEOUT],
    sessionTimeout
  );
}

export function* onLoggedIn() {
  yield race({
    loggedIn: call(watcherWhenLoggedIn),
    logout: take(ActionTypes.LOGGED_OFF),
  });
}

export function* onFetchStatusSuccess(action: FetchStatusSuccessAction): SagaIterator {
  try {
    const lastSession = yield select(rootSelectors.authentication.getLastSession);

    // When the user refreshes the page, status will automatically update the
    // store. However, we don't known when the current session started. As
    // LOGIN_SUCCESS and RENEW_SUCCESS both set lastSession, we can assume that
    // the user refreshed.
    if (!lastSession) {
      yield put(renewSessionRequest(rootSelectors.authentication.getToken));

      const { success } = yield race({
        success: take(ActionTypes.FETCH_RENEW_SESSION_SUCCESS),
        failure: take(ActionTypes.FETCH_RENEW_SESSION_FAILURE),
      });

      if (success) {
        yield put(loggedIn());
        yield put(manageSessionTimeout());
      } else {
        yield put(loggedOff());
      }
    } else {
      yield put(loggedIn());
    }
  } catch (err) {
    // do nothing
  }
}

export default function* rootSaga() {
  yield all([
    takeLatest(ActionTypes.FETCH_LOGIN_REQUEST, onLoginRequest),
    takeLatest(ActionTypes.FETCH_LOGOUT_REQUEST, onLogoutRequest),
    takeLatest(ActionTypes.LOGGED_IN, onLoggedIn),
    takeLatest(ActionTypes.FETCH_RENEW_SESSION_REQUEST, onRenewSessionRequest),
    takeLatest(RootActionTypes.api.FETCH_STATUS_SUCCESS, onFetchStatusSuccess),
  ]);
}

/**
 * Used for testing only.
 */
export const internalTesting = {
  constants,
};
